const stats = [
  { icon: 'ri-award-line', value: 'PADI', label: 'Accredited Dive Centre' },
  { icon: 'ri-star-fill', value: '4.8★', label: '249+ Google Reviews' },
  { icon: 'ri-ship-2-line', value: 'MV Mahi-Mahi', label: 'Our Reef Vessel' },
  { icon: 'ri-map-pin-2-line', value: '45–60 min', label: 'To Outer Reef' },
];

export default function AboutSection() {
  return (
    <section id="about" className="w-full bg-white py-24 px-6 md:px-10">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Text */}
          <div>
            <p className="text-xs uppercase tracking-[3px] text-cyan-500 font-semibold mb-4">About Us</p>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 leading-tight mb-6">
              Mission Beach Dive Adventures
            </h2>
            <p className="text-gray-500 text-base leading-relaxed mb-5 font-light">
              We're a locally owned and operated PADI dive centre based at Clump Point Jetty, Mission Beach, Queensland. For years we've been taking guests from Bedarra Island Resort and beyond out to the pristine outer Great Barrier Reef aboard our purpose-built vessel, MV Mahi-Mahi.
            </p>
            <p className="text-gray-500 text-base leading-relaxed mb-5 font-light">
              Whether you're a seasoned certified diver chasing two reef sites, a complete first-timer ready for your PADI Discover Scuba experience, or simply want to float above the coral on a guided snorkel — there's a place on the boat for you.
            </p>
            <p className="text-gray-500 text-base leading-relaxed mb-8 font-light">
              Our crew are passionate about the reef, deeply knowledgeable about local marine life, and committed to small group sizes so every guest gets a personal, unforgettable experience. We're not a cattle boat — we're your guides to one of the world's great natural wonders.
            </p>

            <div className="flex flex-wrap gap-3">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-50 border border-cyan-100 text-cyan-700 text-sm font-medium">
                <i className="ri-shield-check-line"></i>GBRMPA Permitted Operator
              </div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-50 border border-cyan-100 text-cyan-700 text-sm font-medium">
                <i className="ri-medal-line"></i>PADI Dive Centre
              </div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-50 border border-cyan-100 text-cyan-700 text-sm font-medium">
                <i className="ri-map-pin-2-line"></i>Locally Owned
              </div>
            </div>
          </div>

          {/* Image + Stats */}
          <div className="flex flex-col gap-6">
            <div className="w-full h-72 md:h-96 rounded-2xl overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/9bf524176007ea226bfc0005a932d605.jpg"
                alt="Certified dive tour on the Great Barrier Reef"
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              {stats.map((s) => (
                <div key={s.label} className="flex flex-col items-center text-center bg-gray-50 rounded-2xl p-5 border border-gray-100">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-cyan-50 text-cyan-500 mb-3">
                    <i className={`${s.icon} text-xl`}></i>
                  </div>
                  <span className="text-lg font-black text-gray-900">{s.value}</span>
                  <span className="text-xs text-gray-400 mt-0.5">{s.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* How it works */}
        <div className="mt-24">
          <div className="text-center mb-14">
            <p className="text-xs uppercase tracking-[3px] text-cyan-500 font-semibold mb-4">The Logistics</p>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 leading-tight mb-4">
              From Bedarra Island to the Reef — Here's How It Works
            </h2>
            <p className="text-gray-500 text-base max-w-lg mx-auto font-light leading-relaxed">
              Scuba diving from Bedarra Island is easier than you think. Four steps and you're on the outer reef.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { num: '01', icon: 'ri-water-flash-line', title: 'Book in Advance', desc: 'Lock in your preferred date online or by phone — we recommend at least 48 hours ahead, especially in peak season. Tell us your experience level and we\'ll sort the rest.' },
              { num: '02', icon: 'ri-sailboat-line', title: 'Water Taxi to Mission Beach', desc: 'Take the Bedarra Island water taxi across to Mission Beach. It\'s a short, easy transfer — your reef day starts the moment you hit the water.' },
              { num: '03', icon: 'ri-ship-2-line', title: 'Board MV Mahi-Mahi at Clump Point', desc: 'Meet your crew at Clump Point Jetty and board MV Mahi-Mahi. Briefings done, gear sorted, and you\'re outbound to the outer Great Barrier Reef.' },
              { num: '04', icon: 'ri-bubble-chart-line', title: 'Dive or Snorkel Beaver Reef', desc: '45–60 minutes offshore, Beaver Reef awaits. Certified divers go deep, first-timers go with a PADI instructor, snorkellers float above it all.' },
            ].map((step, i) => (
              <div key={step.num} className="relative flex flex-col bg-white rounded-2xl p-7 border border-cyan-100">
                <span className="text-5xl font-black text-cyan-100 leading-none mb-4 select-none">{step.num}</span>
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-cyan-50 text-cyan-500 mb-4">
                  <i className={`${step.icon} text-xl`}></i>
                </div>
                <h3 className="text-base font-bold text-gray-900 mb-2 leading-snug">{step.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
                {i < 3 && (
                  <div className="hidden lg:flex absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 items-center justify-center text-cyan-300 z-10">
                    <i className="ri-arrow-right-s-line text-2xl w-6 h-6 flex items-center justify-center"></i>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://wa.me/61455957263?text=Hi!%20I'd%20like%20to%20book%20a%20dive%20trip%20to%20Bedarra%20Island."
              target="_blank"
              rel="noopener noreferrer"
              className="whitespace-nowrap inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold text-sm cursor-pointer hover:scale-105 transition-transform duration-300"
            >
              <i className="ri-calendar-check-line w-4 h-4 flex items-center justify-center"></i>
              Book Your Dive Day Trip
            </a>
            <a
              href="tel:0455957263"
              className="whitespace-nowrap inline-flex items-center gap-2 px-7 py-4 rounded-full border-2 border-gray-300 text-gray-600 font-semibold text-sm cursor-pointer hover:border-gray-500 hover:text-gray-800 transition-all duration-200"
            >
              <i className="ri-phone-line w-4 h-4 flex items-center justify-center"></i>
              Call Us: 0455 957 263
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
